import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './VirtualClassroomPage.css';

const API_URL = 'http://vtym-douane.test/backend/wp-json';

const VirtualClassroomPage = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { user, getToken } = useAuth();
    
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const jitsiContainerRef = useRef(null);
    const jitsiApiRef = useRef(null);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        loadSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, sessionId]);

    const loadSession = async () => {
        try {
            const token = getToken();
            
            // Charger les infos de la session
            const response = await fetch(`${API_URL}/douane/v1/sessions/${sessionId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            const data = await response.json();
            
            if (data.success) {
                setSession(data.session);
                setHasAccess(data.has_access);
                
                // Initialiser Jitsi Meet
                if (data.has_access) {
                    initJitsi(data.session);
                }
            } else {
                alert('Session non trouvée ou accès refusé');
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Erreur:', err);
            alert('Impossible de charger la session');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const initJitsi = (sessionData) => {
        // Charger le script Jitsi Meet
        if (!window.JitsiMeetExternalAPI) {
            const script = document.createElement('script');
            script.src = 'https://meet.jit.si/external_api.js';
            script.async = true;
            script.onload = () => createJitsiMeeting(sessionData);
            document.body.appendChild(script);
        } else {
            createJitsiMeeting(sessionData);
        }
    };

    const createJitsiMeeting = (sessionData) => {
        const domain = 'meet.jit.si';
        const roomName = `vtym-douane-${sessionData.id}-${sessionData.formation_id}`;
        
        const options = {
            roomName: roomName,
            width: '100%',
            height: 600,
            parentNode: jitsiContainerRef.current,
            configOverwrite: {
                startWithAudioMuted: true,
                startWithVideoMuted: false,
                enableWelcomePage: false,
                prejoinPageEnabled: true,
                disableDeepLinking: true,
            },
            interfaceConfigOverwrite: {
                TOOLBAR_BUTTONS: [
                    'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
                    'fodeviceselection', 'hangup', 'chat', 'recording',
                    'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
                    'videoquality', 'filmstrip', 'invite', 'feedback', 'stats',
                    'shortcuts', 'tileview', 'download', 'help', 'mute-everyone',
                ],
                SHOW_JITSI_WATERMARK: false,
                SHOW_WATERMARK_FOR_GUESTS: false,
            },
            userInfo: {
                displayName: user?.name || user?.email || 'Participant',
            }
        };

        jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, options);

        // Événements
        jitsiApiRef.current.addEventListener('videoConferenceJoined', () => {
            console.log('✅ Rejoint la visioconférence');
        });

        jitsiApiRef.current.addEventListener('videoConferenceLeft', () => {
            console.log('👋 A quitté la visioconférence');
            navigate('/dashboard');
        });

        jitsiApiRef.current.addEventListener('readyToClose', () => {
            navigate('/dashboard');
        });
    };

    const leaveSession = () => {
        if (jitsiApiRef.current) {
            jitsiApiRef.current.dispose();
        }
        navigate('/dashboard');
    };

    if (loading) {
        return (
            <div className="virtual-classroom-page">
                <div className="container">
                    <div className="loading">Chargement de la salle de classe...</div>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="virtual-classroom-page">
                <div className="container">
                    <div className="access-denied">
                        <h2>❌ Accès refusé</h2>
                        <p>Vous n'avez pas accès à cette session.</p>
                        <button onClick={() => navigate('/dashboard')} className="btn-back">
                            Retour au Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="virtual-classroom-page">
            <div className="container">
                
                {/* Header Session */}
                <div className="session-header">
                    <div className="session-info">
                        <h1>📹 {session?.titre || 'Classe Virtuelle'}</h1>
                        <p className="formation-name">{session?.formation_titre}</p>
                        <div className="session-meta">
                            <span className="meta-item">
                                👨‍🏫 {session?.formateur || 'Formateur'}
                            </span>
                            <span className="meta-item">
                                📅 {new Date(session?.date_debut).toLocaleString('fr-FR')}
                            </span>
                            <span className="meta-item live">
                                🔴 En direct
                            </span>
                        </div>
                    </div>
                    <button onClick={leaveSession} className="btn-leave">
                        Quitter la session
                    </button>
                </div>

                {/* Conteneur Jitsi */}
                <div className="jitsi-container" ref={jitsiContainerRef}></div>

                {/* Informations supplémentaires */}
                <div className="session-footer">
                    <div className="info-cards">
                        <div className="info-card">
                            <div className="info-icon">💬</div>
                            <h3>Chat disponible</h3>
                            <p>Utilisez le chat pour poser vos questions</p>
                        </div>
                        <div className="info-card">
                            <div className="info-icon">🖥️</div>
                            <h3>Partage d'écran</h3>
                            <p>Le formateur peut partager son écran</p>
                        </div>
                        <div className="info-card">
                            <div className="info-icon">📝</div>
                            <h3>Enregistrement</h3>
                            <p>Cette session peut être enregistrée</p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default VirtualClassroomPage;